import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Barcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';
import QRCode from 'qrcode';
import {
  Tag, Printer, Bike, User as UserIcon, Wrench, Search, Eye, Plus, Minus, X, ChevronDown,
  ChevronLeft, ChevronRight, ListPlus, Trash2, RectangleVertical, RectangleHorizontal,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brother QL-820NWBc with DK-22210 continuous tape — 29mm wide; user picks the cut length.
const LABEL_W_MM = 29;
const DEFAULT_LABEL_H_MM = 26;  // shorter than original 30 but keeps room for big number + QR
const MIN_LABEL_H_MM = 15;
const MAX_LABEL_H_MM = 60;

// Equal margins on the 4 sides
const PRINT_MARGIN_MM = 1.5;

const TEMPLATES = [
  { key: 'bicycle', label: 'Bicicleta', icon: Bike, desc: 'Nº bici + barcode · multi-selección por bici o modelo' },
  { key: 'name', label: 'Nombre', icon: UserIcon, desc: 'Lista de nombres · 1 etiqueta por nombre' },
  { key: 'accessory', label: 'Accesorio', icon: Wrench, desc: 'Multi-selección de accesorios + manual' },
];

const STORAGE_KEY = 'veloflux_brother_label_v2';

const STATUS_OPTIONS = [
  { value: 'activa',      label: 'Activas',     color: 'bg-blue-100 text-blue-700' },
  { value: 'prestada',    label: 'Prestadas',   color: 'bg-purple-100 text-purple-700' },
  { value: 'en_revision', label: 'En revisión', color: 'bg-amber-100 text-amber-700' },
  { value: 'en_taller',   label: 'En taller',   color: 'bg-orange-100 text-orange-700' },
  { value: 'rota',        label: 'Rotas',       color: 'bg-red-100 text-red-700' },
  { value: 'robada',      label: 'Robadas',     color: 'bg-pink-100 text-pink-700' },
  { value: 'vendida',     label: 'Vendidas',    color: 'bg-green-100 text-green-700' },
  { value: 'retirada',    label: 'Retiradas',   color: 'bg-slate-200 text-slate-700' },
];

export default function BrotherLabels() {
  const [activeTpl, setActiveTpl] = useState('bicycle');
  const [copiesPerItem, setCopiesPerItem] = useState(1);
  const [orientation, setOrientation] = useState('vertical'); // 'vertical' | 'horizontal'
  const [labelHeightMm, setLabelHeightMm] = useState(DEFAULT_LABEL_H_MM);

  // Bicycle state
  const [bikes, setBikes] = useState([]);
  const [bikeSearch, setBikeSearch] = useState('');
  const [bikePopoverOpen, setBikePopoverOpen] = useState(false);
  const [selectedBikeIds, setSelectedBikeIds] = useState([]); // multi
  const [modelFilter, setModelFilter] = useState([]); // multi
  const [modelPopoverOpen, setModelPopoverOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState([]); // multi — empty = all statuses
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);

  // Name state
  const [names, setNames] = useState([]); // array of strings
  const [nameInput, setNameInput] = useState('');
  const [bulkPasteOpen, setBulkPasteOpen] = useState(false);
  const [bulkPasteText, setBulkPasteText] = useState('');

  // Accessory state
  const [accesorios, setAccesorios] = useState([]);
  const [accSearch, setAccSearch] = useState('');
  const [accPopoverOpen, setAccPopoverOpen] = useState(false);
  const [selectedAccIds, setSelectedAccIds] = useState([]); // multi
  const [manualAccItems, setManualAccItems] = useState([]); // [{name, barcode}]
  const [manualAccText, setManualAccText] = useState('');
  const [manualAccBarcode, setManualAccBarcode] = useState('');

  // Preview navigation
  const [previewIdx, setPreviewIdx] = useState(0);

  // Restore
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (data.activeTpl) setActiveTpl(data.activeTpl);
        if (Array.isArray(data.names)) setNames(data.names);
        if (Array.isArray(data.manualAccItems)) setManualAccItems(data.manualAccItems);
        if (data.orientation) setOrientation(data.orientation);
        if (typeof data.labelHeightMm === 'number') setLabelHeightMm(data.labelHeightMm);
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ activeTpl, names, manualAccItems, orientation, labelHeightMm }));
    } catch {}
  }, [activeTpl, names, manualAccItems, orientation, labelHeightMm]);

  useEffect(() => {
    fetchBikes();
    fetchAccesorios();
  }, []);

  // Reset preview index when active items change
  useEffect(() => { setPreviewIdx(0); }, [activeTpl]);

  const fetchBikes = async () => {
    try {
      const res = await axios.get(`${API}/bicycles`);
      setBikes(res.data || []);
    } catch {}
  };
  const fetchAccesorios = async () => {
    try {
      const res = await axios.get(`${API}/accesorios`);
      setAccesorios(res.data || []);
    } catch {}
  };

  // ===== Bicycle helpers =====
  const modelOptions = useMemo(() => {
    const set = new Set();
    bikes.forEach(b => { if (b.model) set.add(b.model); });
    return Array.from(set).sort();
  }, [bikes]);

  const filteredBikes = useMemo(() => {
    const q = bikeSearch.trim().toLowerCase();
    return bikes.filter(b => {
      if (modelFilter.length > 0 && !modelFilter.includes(b.model)) return false;
      if (statusFilter.length > 0 && !statusFilter.includes(b.status)) return false;
      if (q && !`${b.number} ${b.model} ${b.frame_number || ''}`.toLowerCase().includes(q)) return false;
      return true;
    }).slice(0, 500);
  }, [bikes, bikeSearch, modelFilter, statusFilter]);

  const selectedBikes = useMemo(
    () => selectedBikeIds.map(id => bikes.find(b => b.id === id)).filter(Boolean),
    [selectedBikeIds, bikes],
  );

  const toggleBike = (id) => {
    setSelectedBikeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const selectAllFiltered = () => {
    const ids = filteredBikes.map(b => b.id);
    setSelectedBikeIds(prev => Array.from(new Set([...prev, ...ids])));
  };
  const clearBikes = () => setSelectedBikeIds([]);

  // ===== Accessory helpers =====
  const filteredAccs = useMemo(() => {
    const q = accSearch.trim().toLowerCase();
    return accesorios.filter(a =>
      !q || `${a.name || ''} ${a.codigo_barras || a.barcode || ''}`.toLowerCase().includes(q),
    ).slice(0, 500);
  }, [accesorios, accSearch]);

  const selectedAccs = useMemo(
    () => selectedAccIds.map(id => accesorios.find(a => a.id === id)).filter(Boolean),
    [selectedAccIds, accesorios],
  );

  const toggleAcc = (id) => {
    setSelectedAccIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const addManualAcc = () => {
    const name = manualAccText.trim();
    const barcode = manualAccBarcode.trim();
    if (!name && !barcode) return toast.error('Introduce nombre o código');
    setManualAccItems(prev => [...prev, { name, barcode }]);
    setManualAccText('');
    setManualAccBarcode('');
  };
  const removeManualAcc = (i) => setManualAccItems(prev => prev.filter((_, idx) => idx !== i));

  // ===== Name helpers =====
  const addName = () => {
    const v = nameInput.trim();
    if (!v) return;
    setNames(prev => [...prev, v]);
    setNameInput('');
  };
  const removeName = (i) => setNames(prev => prev.filter((_, idx) => idx !== i));
  const clearNames = () => setNames([]);
  const applyBulkPaste = () => {
    const lines = bulkPasteText.split('\n').map(s => s.trim()).filter(Boolean);
    if (!lines.length) return toast.error('Pega al menos un nombre');
    setNames(prev => [...prev, ...lines]);
    setBulkPasteText('');
    setBulkPasteOpen(false);
    toast.success(`${lines.length} nombre(s) añadido(s)`);
  };

  // ===== Build the list of labels to print =====
  const labelsToPrint = useMemo(() => {
    if (activeTpl === 'bicycle') {
      return selectedBikes.map(b => {
        // Bike numbers may have format "<modelo>(<talla>)<secuencial>" (e.g. "1(53)02").
        // Show only the trailing sequential part on top; talla goes below from bike.size.
        // Numbers without that pattern are shown as-is — no zero padding ever.
        const raw = String(b.number ?? '');
        const m = raw.match(/\)([^)]+)$/);
        const displayNumber = m ? m[1] : raw;
        return {
          type: 'bicycle',
          primary: displayNumber,
          size: b.size || '',
          barcode: b.frame_number || raw,
          key: b.id,
          subtitle: b.model,
        };
      });
    }
    if (activeTpl === 'name') {
      return names.map((n, i) => ({ type: 'name', primary: n, key: `name-${i}` }));
    }
    if (activeTpl === 'accessory') {
      const fromDb = selectedAccs.map(a => ({
        type: 'accessory',
        primary: a.name || '',
        barcode: a.codigo_barras || a.barcode || '',
        key: a.id,
      }));
      const manuals = manualAccItems.map((m, i) => ({
        type: 'accessory',
        primary: m.name,
        barcode: m.barcode,
        key: `manual-${i}`,
      }));
      return [...fromDb, ...manuals];
    }
    return [];
  }, [activeTpl, selectedBikes, names, selectedAccs, manualAccItems]);

  const totalLabels = labelsToPrint.length * Math.max(1, copiesPerItem);
  const currentPreview = labelsToPrint[Math.min(previewIdx, Math.max(0, labelsToPrint.length - 1))] || null;

  const print = async () => {
    if (labelsToPrint.length === 0) return toast.error('Selecciona o añade al menos un elemento');
    const cpy = Math.max(1, Math.min(parseInt(copiesPerItem) || 1, 200));
    const win = window.open('', '_blank', 'width=400,height=500');
    if (!win) return toast.error('Bloqueador de ventanas activo');

    // Page dimensions according to orientation. labelHeightMm is the user-chosen cut length.
    const isHoriz = orientation === 'horizontal';
    const pageW = isHoriz ? labelHeightMm : LABEL_W_MM;
    const pageH = isHoriz ? LABEL_W_MM : labelHeightMm;
    const sizeKw = isHoriz ? 'landscape' : 'portrait';

    // Pre-generate QR SVG strings here (where qrcode is guaranteed loaded),
    // then embed them as static SVG in the print HTML. No CDN, no race conditions —
    // the printer driver receives ready-to-rasterise SVG markup.
    const qrCache = {};
    const uniqueQrValues = Array.from(new Set(
      labelsToPrint.filter(l => l.type === 'bicycle' && l.barcode).map(l => String(l.barcode))
    ));
    await Promise.all(uniqueQrValues.map(async (v) => {
      try {
        qrCache[v] = await QRCode.toString(v, {
          type: 'svg',
          errorCorrectionLevel: 'M',
          margin: 2,
          color: { dark: '#000000', light: '#FFFFFF' },
        });
      } catch (e) {
        qrCache[v] = '';
      }
    }));

    // Expand each label × copies
    const expanded = labelsToPrint.flatMap(l => Array.from({ length: cpy }, () => l));
    const contentLabelHmm = labelHeightMm;
    const pages = expanded.map(l => {
      const cls = (l.type === 'bicycle') ? 'label' : 'label center-content';
      return `<div class="${cls}">${renderLabelHtml(l, contentLabelHmm, qrCache)}</div>`;
    }).join('');

    win.document.write(`<!doctype html>
<html><head><meta charset="utf-8"/>
<title>Etiquetas Brother (${expanded.length})</title>
<style>
  @page { size: ${pageW}mm ${pageH}mm ${sizeKw}; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; color: #000; }
  .label {
    width: ${pageW}mm;
    height: ${pageH}mm;
    padding: ${PRINT_MARGIN_MM}mm;
    page-break-after: always;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    text-align: center;
    overflow: hidden;
  }
  /* For Name and Accessory templates we want vertical centering (no big number anchored at top) */
  .label.center-content { justify-content: center; }
  .label:last-child { page-break-after: auto; }
  .number-big { font-weight: 900; line-height: 0.95; letter-spacing: -0.3mm; white-space: nowrap; }
  .bike-stack { display: flex; flex-direction: column; align-items: center; gap: 0.4mm; }
  .bike-size { font-weight: 700; line-height: 1; letter-spacing: 0.2mm; color: #0f172a; }
  .bike-qr { padding-top: 0.2mm; display: flex; justify-content: center; }
  .qr-wrap { width: 100%; display: flex; justify-content: center; }
  .qr-canvas canvas, .qr-canvas svg { width: 100% !important; height: 100% !important; display: block; }
  .name-text { font-weight: 700; line-height: 1.1; word-break: break-word; display: flex; flex-direction: column; align-items: center; justify-content: center; width: 100%; }
  .name-line { line-height: 1.1; }
  .acc-name { font-weight: 700; line-height: 1.05; word-break: break-word; }
  .barcode-wrap { width: 100%; display: flex; justify-content: center; }
  svg.barcode { width: 100%; height: auto; display: block; }
  @media print { .label { padding: ${PRINT_MARGIN_MM}mm; } }
</style>
</head>
<body>
${pages}
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"><\/script>
<script>
  (function(){
    try {
      // 1D barcodes (accessory template). QR codes for bicycles are already pre-rendered
      // as inline SVG by the main page — no extra rendering step needed.
      document.querySelectorAll('svg.barcode').forEach(function(svg){
        var value = svg.getAttribute('data-value') || '';
        if (!value) return;
        JsBarcode(svg, value, {
          format: 'CODE128',
          width: ${isHoriz ? 2.0 : 2.4},
          height: ${isHoriz ? 90 : 110},
          margin: 8,
          displayValue: false
        });
      });
    } catch(e) { console.error(e); }
    // Static content — give the browser a beat to paint, then print.
    setTimeout(function(){ window.print(); }, 250);
    window.onafterprint = function(){ window.close(); };
  })();
<\/script>
</body></html>`);
    win.document.close();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-semibold text-slate-800 flex items-center gap-2">
            <Tag size={28} className="text-orange-500" />
            Etiquetas Brother QL-820NWBc
          </h1>
          <p className="text-sm text-slate-500">
            Rollo DK-22210 · {orientation === 'vertical' ? `${LABEL_W_MM}×${labelHeightMm}` : `${labelHeightMm}×${LABEL_W_MM}`}mm · Corte automático entre etiquetas · Multi-selección
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="orientation-toggle">
          <button
            type="button"
            onClick={() => setOrientation('vertical')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              orientation === 'vertical' ? 'bg-orange-500 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="orientation-vertical"
          >
            <RectangleVertical size={14} />
            Vertical
          </button>
          <button
            type="button"
            onClick={() => setOrientation('horizontal')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              orientation === 'horizontal' ? 'bg-orange-500 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="orientation-horizontal"
          >
            <RectangleHorizontal size={14} />
            Horizontal
          </button>
        </div>
      </div>

      {/* Template selector */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {TEMPLATES.map(t => {
          const Icon = t.icon;
          const active = activeTpl === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTpl(t.key)}
              className={`text-left p-4 rounded-lg border-2 transition-all ${
                active ? 'border-orange-500 bg-orange-50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid={`tpl-${t.key}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={18} className={active ? 'text-orange-600' : 'text-slate-500'} />
                <span className={`font-semibold ${active ? 'text-orange-700' : 'text-slate-700'}`}>{t.label}</span>
              </div>
              <p className="text-xs text-slate-500">{t.desc}</p>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Form */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-600 uppercase tracking-wider">
              Selección
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {activeTpl === 'bicycle' && (
              <BicycleSelector
                bikes={bikes}
                filteredBikes={filteredBikes}
                modelOptions={modelOptions}
                modelFilter={modelFilter}
                setModelFilter={setModelFilter}
                modelPopoverOpen={modelPopoverOpen}
                setModelPopoverOpen={setModelPopoverOpen}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                statusPopoverOpen={statusPopoverOpen}
                setStatusPopoverOpen={setStatusPopoverOpen}
                bikePopoverOpen={bikePopoverOpen}
                setBikePopoverOpen={setBikePopoverOpen}
                bikeSearch={bikeSearch}
                setBikeSearch={setBikeSearch}
                selectedBikeIds={selectedBikeIds}
                selectedBikes={selectedBikes}
                toggleBike={toggleBike}
                selectAllFiltered={selectAllFiltered}
                clearBikes={clearBikes}
                removeBike={(id) => setSelectedBikeIds(prev => prev.filter(x => x !== id))}
              />
            )}

            {activeTpl === 'name' && (
              <NameEditor
                names={names}
                nameInput={nameInput}
                setNameInput={setNameInput}
                addName={addName}
                removeName={removeName}
                clearNames={clearNames}
                bulkPasteOpen={bulkPasteOpen}
                setBulkPasteOpen={setBulkPasteOpen}
                bulkPasteText={bulkPasteText}
                setBulkPasteText={setBulkPasteText}
                applyBulkPaste={applyBulkPaste}
              />
            )}

            {activeTpl === 'accessory' && (
              <AccessoryEditor
                filteredAccs={filteredAccs}
                accSearch={accSearch}
                setAccSearch={setAccSearch}
                accPopoverOpen={accPopoverOpen}
                setAccPopoverOpen={setAccPopoverOpen}
                selectedAccIds={selectedAccIds}
                selectedAccs={selectedAccs}
                toggleAcc={toggleAcc}
                clearAccs={() => setSelectedAccIds([])}
                manualAccItems={manualAccItems}
                manualAccText={manualAccText}
                setManualAccText={setManualAccText}
                manualAccBarcode={manualAccBarcode}
                setManualAccBarcode={setManualAccBarcode}
                addManualAcc={addManualAcc}
                removeManualAcc={removeManualAcc}
              />
            )}

            <div className="pt-3 border-t border-slate-200 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Copias por elemento</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setCopiesPerItem(Math.max(1, copiesPerItem - 1))} data-testid="qty-minus"><Minus size={14} /></Button>
                    <Input
                      type="number"
                      min="1"
                      max="200"
                      value={copiesPerItem}
                      onChange={(e) => setCopiesPerItem(Math.max(1, Math.min(parseInt(e.target.value) || 1, 200)))}
                      className="h-8 text-center"
                      data-testid="qty-input"
                    />
                    <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setCopiesPerItem(Math.min(200, copiesPerItem + 1))} data-testid="qty-plus"><Plus size={14} /></Button>
                  </div>
                </div>
                <div>
                  <Label>Largo etiqueta (mm)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Button
                      size="sm" variant="outline" className="h-8 w-8 p-0"
                      onClick={() => setLabelHeightMm(Math.max(MIN_LABEL_H_MM, labelHeightMm - 1))}
                      data-testid="length-minus"
                    ><Minus size={14} /></Button>
                    <Input
                      type="number"
                      min={MIN_LABEL_H_MM}
                      max={MAX_LABEL_H_MM}
                      value={labelHeightMm}
                      onChange={(e) => setLabelHeightMm(Math.max(MIN_LABEL_H_MM, Math.min(parseInt(e.target.value) || DEFAULT_LABEL_H_MM, MAX_LABEL_H_MM)))}
                      className="h-8 text-center"
                      data-testid="length-input"
                    />
                    <Button
                      size="sm" variant="outline" className="h-8 w-8 p-0"
                      onClick={() => setLabelHeightMm(Math.min(MAX_LABEL_H_MM, labelHeightMm + 1))}
                      data-testid="length-plus"
                    ><Plus size={14} /></Button>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                {labelsToPrint.length} elemento(s) × {copiesPerItem} ={' '}
                <span className="font-semibold text-slate-700" data-testid="total-labels">{totalLabels}</span> etiqueta(s) · {labelHeightMm}mm de largo
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-2">
              <Eye size={14} /> Vista previa (1:1)
            </CardTitle>
            <p className="text-xs text-slate-500">
              {orientation === 'vertical' ? `${LABEL_W_MM}×${labelHeightMm}` : `${labelHeightMm}×${LABEL_W_MM}`} mm · {orientation === 'vertical' ? 'Vertical' : 'Horizontal'} · Brother QL-820NWBc · DK-22210
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center bg-slate-100 rounded-lg p-6 relative">
              <LabelPreview data={currentPreview} orientation={orientation} labelHeightMm={labelHeightMm} />
              {labelsToPrint.length > 1 && (
                <>
                  <Button
                    size="sm" variant="outline" className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0 rounded-full bg-white"
                    onClick={() => setPreviewIdx((previewIdx - 1 + labelsToPrint.length) % labelsToPrint.length)}
                    data-testid="preview-prev"
                  ><ChevronLeft size={14} /></Button>
                  <Button
                    size="sm" variant="outline" className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0 rounded-full bg-white"
                    onClick={() => setPreviewIdx((previewIdx + 1) % labelsToPrint.length)}
                    data-testid="preview-next"
                  ><ChevronRight size={14} /></Button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[10px] text-slate-500 bg-white px-2 py-0.5 rounded-full border">
                    {previewIdx + 1} / {labelsToPrint.length}
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-center mt-4">
              <Button
                onClick={print}
                disabled={labelsToPrint.length === 0}
                className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
                data-testid="brother-print-btn"
              >
                <Printer size={16} />
                Imprimir {totalLabels > 0 ? `${totalLabels} etiqueta${totalLabels !== 1 ? 's' : ''}` : ''}
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 text-center mt-2">
              Configura la impresora del navegador como Brother QL-820NWBc con rollo DK-22210 y activa "Corte automático" para que corte entre cada etiqueta.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ===================== Bicycle selector subcomponent =====================

function BicycleSelector({
  bikes, filteredBikes, modelOptions, modelFilter, setModelFilter,
  modelPopoverOpen, setModelPopoverOpen,
  statusFilter, setStatusFilter, statusPopoverOpen, setStatusPopoverOpen,
  bikePopoverOpen, setBikePopoverOpen,
  bikeSearch, setBikeSearch,
  selectedBikeIds, selectedBikes,
  toggleBike, selectAllFiltered, clearBikes, removeBike,
}) {
  const toggleModel = (m) => setModelFilter(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  const toggleStatus = (s) => setStatusFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const statusLabel = statusFilter.length === 0
    ? 'Todos los estados'
    : statusFilter.length === 1
      ? STATUS_OPTIONS.find(o => o.value === statusFilter[0])?.label || statusFilter[0]
      : `${statusFilter.length} estados`;
  return (
    <>
      <div className="space-y-2">
        <Label>Filtrar por estado (opcional)</Label>
        <Popover open={statusPopoverOpen} onOpenChange={setStatusPopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-between h-9 font-normal" data-testid="status-filter-trigger">
              <span className="truncate">{statusLabel}</span>
              <ChevronDown size={14} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <div className="p-2 border-b border-slate-200 flex items-center justify-between">
              <span className="text-xs text-slate-500">{STATUS_OPTIONS.length} estados</span>
              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setStatusFilter([])} data-testid="status-filter-clear">Todas</Button>
            </div>
            <div className="max-h-72 overflow-y-auto p-1">
              {STATUS_OPTIONS.map(opt => {
                const count = bikes.filter(b => b.status === opt.value).length;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleStatus(opt.value)}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-50 text-sm flex items-center gap-2"
                    data-testid={`status-opt-${opt.value}`}
                  >
                    <Checkbox checked={statusFilter.includes(opt.value)} />
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${opt.color}`}>{opt.label}</span>
                    <span className="ml-auto text-xs text-slate-400">{count}</span>
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="space-y-2">
        <Label>Filtrar por modelo (opcional)</Label>
        <Popover open={modelPopoverOpen} onOpenChange={setModelPopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-between h-9 font-normal" data-testid="model-filter-trigger">
              <span className="truncate">
                {modelFilter.length === 0 ? 'Todos los modelos' : modelFilter.length === 1 ? modelFilter[0] : `${modelFilter.length} modelos`}
              </span>
              <ChevronDown size={14} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96 p-0" align="start">
            <div className="p-2 border-b border-slate-200 flex items-center justify-between">
              <span className="text-xs text-slate-500">{modelOptions.length} modelos</span>
              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setModelFilter([])}>Limpiar</Button>
            </div>
            <div className="max-h-64 overflow-y-auto p-1">
              {modelOptions.map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleModel(m)}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-50 text-sm flex items-center gap-2"
                  data-testid={`model-opt-${m}`}
                >
                  <Checkbox checked={modelFilter.includes(m)} />
                  <span className="truncate">{m}</span>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Bicicletas a etiquetar</Label>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={selectAllFiltered} data-testid="bike-select-filtered" disabled={filteredBikes.length === 0}>
              + filtradas ({filteredBikes.length})
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={clearBikes} disabled={selectedBikeIds.length === 0} data-testid="bike-clear">
              Limpiar
            </Button>
          </div>
        </div>
        <Popover open={bikePopoverOpen} onOpenChange={setBikePopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-between h-9 font-normal" data-testid="bike-selector">
              <span className="truncate">
                {selectedBikeIds.length === 0 ? 'Selecciona bicicletas (multi)' : `${selectedBikeIds.length} bicicleta(s) seleccionada(s)`}
              </span>
              <ChevronDown size={14} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[420px] p-0" align="start">
            <div className="p-2 border-b border-slate-200">
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  autoFocus
                  placeholder="Buscar por nº, modelo, bastidor..."
                  value={bikeSearch}
                  onChange={(e) => setBikeSearch(e.target.value)}
                  className="pl-7 h-8 text-sm"
                  data-testid="bike-search"
                />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto p-1">
              {filteredBikes.map(b => {
                const checked = selectedBikeIds.includes(b.id);
                const statusOpt = STATUS_OPTIONS.find(o => o.value === b.status);
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => toggleBike(b.id)}
                    className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 ${checked ? 'bg-orange-50' : 'hover:bg-slate-50'}`}
                    data-testid={`bike-option-${b.id}`}
                  >
                    <Checkbox checked={checked} />
                    <span className="font-mono font-semibold text-slate-800 min-w-[70px]">{b.number}</span>
                    <span className="text-slate-500 truncate flex-1">{b.model}</span>
                    {statusOpt && (
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${statusOpt.color}`}>{statusOpt.label}</span>
                    )}
                    <span className="text-xs text-slate-400">{b.size || ''}</span>
                  </button>
                );
              })}
              {filteredBikes.length === 0 && <div className="text-center text-xs text-slate-400 py-3">Sin resultados</div>}
            </div>
          </PopoverContent>
        </Popover>

        {selectedBikes.length > 0 && (
          <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto p-1 bg-slate-50 rounded border border-slate-200" data-testid="selected-bikes-chips">
            {selectedBikes.map(b => (
              <Badge key={b.id} variant="secondary" className="gap-1 font-mono">
                {b.number}
                <button onClick={() => removeBike(b.id)} className="hover:text-red-600" data-testid={`chip-remove-${b.id}`}>
                  <X size={10} />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ===================== Name editor subcomponent =====================

function NameEditor({
  names, nameInput, setNameInput, addName, removeName, clearNames,
  bulkPasteOpen, setBulkPasteOpen, bulkPasteText, setBulkPasteText, applyBulkPaste,
}) {
  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Nombres a etiquetar ({names.length})</Label>
          <div className="flex items-center gap-1">
            <Popover open={bulkPasteOpen} onOpenChange={setBulkPasteOpen}>
              <PopoverTrigger asChild>
                <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" data-testid="bulk-paste-btn">
                  <ListPlus size={12} /> Pegar varios
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80" align="end">
                <div className="space-y-2">
                  <Label className="text-xs">Un nombre por línea</Label>
                  <Textarea
                    rows={6}
                    value={bulkPasteText}
                    onChange={(e) => setBulkPasteText(e.target.value)}
                    placeholder={'Juan García\nMaría López\nPedro Sánchez'}
                    className="text-sm"
                    data-testid="bulk-paste-textarea"
                  />
                  <Button size="sm" onClick={applyBulkPaste} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="bulk-paste-apply">
                    Añadir a la lista
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={clearNames} disabled={names.length === 0} data-testid="names-clear">
              Limpiar
            </Button>
          </div>
        </div>
        <div className="flex gap-2">
          <Input
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addName(); } }}
            placeholder="Juan García López + Enter"
            data-testid="name-input"
          />
          <Button onClick={addName} disabled={!nameInput.trim()} className="bg-orange-500 hover:bg-orange-600" data-testid="name-add-btn">
            <Plus size={14} />
          </Button>
        </div>

        {names.length > 0 && (
          <div className="space-y-1 max-h-64 overflow-y-auto p-1 bg-slate-50 rounded border border-slate-200" data-testid="names-list">
            {names.map((n, i) => (
              <div key={i} className="flex items-center justify-between px-2 py-1 rounded hover:bg-white text-sm group" data-testid={`name-row-${i}`}>
                <span className="truncate">{n}</span>
                <button onClick={() => removeName(i)} className="text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100" data-testid={`name-remove-${i}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ===================== Accessory editor subcomponent =====================

function AccessoryEditor({
  filteredAccs, accSearch, setAccSearch, accPopoverOpen, setAccPopoverOpen,
  selectedAccIds, selectedAccs, toggleAcc, clearAccs,
  manualAccItems, manualAccText, setManualAccText,
  manualAccBarcode, setManualAccBarcode, addManualAcc, removeManualAcc,
}) {
  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Accesorios de la base de datos ({selectedAccIds.length})</Label>
          <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={clearAccs} disabled={selectedAccIds.length === 0} data-testid="acc-clear">
            Limpiar
          </Button>
        </div>
        <Popover open={accPopoverOpen} onOpenChange={setAccPopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-between h-9 font-normal" data-testid="acc-selector">
              <span className="truncate">
                {selectedAccIds.length === 0 ? 'Buscar y seleccionar accesorios' : `${selectedAccIds.length} accesorio(s)`}
              </span>
              <ChevronDown size={14} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[420px] p-0" align="start">
            <div className="p-2 border-b border-slate-200">
              <Input autoFocus placeholder="Buscar..." value={accSearch} onChange={(e) => setAccSearch(e.target.value)} className="h-8 text-sm" data-testid="acc-search" />
            </div>
            <div className="max-h-72 overflow-y-auto p-1">
              {filteredAccs.map(a => {
                const checked = selectedAccIds.includes(a.id);
                const bc = a.codigo_barras || a.barcode;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => toggleAcc(a.id)}
                    className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 ${checked ? 'bg-orange-50' : 'hover:bg-slate-50'}`}
                    data-testid={`acc-option-${a.id}`}
                  >
                    <Checkbox checked={checked} />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{a.name}</div>
                      {bc && <div className="text-xs text-slate-400 font-mono">{bc}</div>}
                    </div>
                  </button>
                );
              })}
              {filteredAccs.length === 0 && <div className="text-center text-xs text-slate-400 py-3">Sin resultados</div>}
            </div>
          </PopoverContent>
        </Popover>

        {selectedAccs.length > 0 && (
          <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto p-1 bg-slate-50 rounded border border-slate-200" data-testid="selected-accs-chips">
            {selectedAccs.map(a => (
              <Badge key={a.id} variant="secondary" className="gap-1">
                {a.name}
                <button onClick={() => toggleAcc(a.id)} className="hover:text-red-600">
                  <X size={10} />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="pt-3 border-t border-slate-200 space-y-2">
        <Label className="text-xs text-slate-500">— o añade manualmente —</Label>
        <Input
          placeholder="Nombre del accesorio"
          value={manualAccText}
          onChange={(e) => setManualAccText(e.target.value)}
          data-testid="acc-name-input"
        />
        <div className="flex gap-2">
          <Input
            placeholder="Código de barras (opcional)"
            value={manualAccBarcode}
            onChange={(e) => setManualAccBarcode(e.target.value)}
            className="font-mono"
            data-testid="acc-barcode-input"
          />
          <Button onClick={addManualAcc} className="bg-orange-500 hover:bg-orange-600" data-testid="acc-manual-add">
            <Plus size={14} />
          </Button>
        </div>

        {manualAccItems.length > 0 && (
          <div className="space-y-1 max-h-32 overflow-y-auto p-1 bg-slate-50 rounded border border-slate-200" data-testid="manual-accs-list">
            {manualAccItems.map((m, i) => (
              <div key={i} className="flex items-center justify-between px-2 py-1 rounded hover:bg-white text-sm group" data-testid={`manual-acc-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="truncate">{m.name || <span className="italic text-slate-400">(sin nombre)</span>}</div>
                  {m.barcode && <div className="text-xs text-slate-400 font-mono">{m.barcode}</div>}
                </div>
                <button onClick={() => removeManualAcc(i)} className="text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ===================== Preview component =====================

const PX_PER_MM = 4;

function LabelPreview({ data, orientation = 'vertical', labelHeightMm = DEFAULT_LABEL_H_MM }) {
  const isHoriz = orientation === 'horizontal';
  const w = (isHoriz ? labelHeightMm : LABEL_W_MM) * PX_PER_MM;
  const h = (isHoriz ? LABEL_W_MM : labelHeightMm) * PX_PER_MM;
  if (!data) {
    return (
      <div
        className="border-2 border-dashed border-slate-300 rounded-md flex items-center justify-center text-xs text-slate-400 px-2 text-center"
        style={{ width: w, height: h }}
      >
        Selecciona elementos para ver la etiqueta
      </div>
    );
  }
  return (
    <div
      className="bg-white border border-slate-300 rounded-sm shadow-sm flex flex-col items-center text-center overflow-hidden"
      style={{
        width: w,
        height: h,
        padding: `${PRINT_MARGIN_MM * PX_PER_MM}px`,
        justifyContent: data.type === 'bicycle' ? 'flex-start' : 'center',
      }}
      data-testid="label-preview"
    >
      {data.type === 'bicycle' && (
        <>
          {(() => {
            const labelHmm = isHoriz ? LABEL_W_MM : labelHeightMm;
            const { numberMm, qrMm } = computeBikeSizes(labelHmm, String(data.primary).length);
            const fontPx = Math.max(10, Math.floor(numberMm * PX_PER_MM));
            return (
              <>
                <div
                  className="leading-none tracking-tight"
                  style={{ fontSize: fontPx, whiteSpace: 'nowrap', fontWeight: 900 }}
                  data-testid="preview-bike-number"
                >
                  {data.primary}
                </div>
                {data.size && (
                  <div
                    className="font-semibold text-slate-800 leading-none mt-0.5"
                    style={{ fontSize: isHoriz ? 13 : 15, letterSpacing: 0.5 }}
                    data-testid="preview-bike-size"
                  >
                    {data.size}
                  </div>
                )}
                {data.barcode && (
                  <div className="pt-0.5 flex items-center justify-center" data-testid="preview-bike-qr">
                    <QRCodeSVG
                      value={String(data.barcode)}
                      size={Math.round(qrMm * PX_PER_MM)}
                      level="M"
                      marginSize={2}
                      bgColor="#FFFFFF"
                      fgColor="#000000"
                    />
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}
      {data.type === 'name' && (() => {
        const words = String(data.primary).split(/\s+/).filter(Boolean);
        const longestLen = words.reduce((m, w) => Math.max(m, w.length), 1);
        const labelHmmEff = isHoriz ? LABEL_W_MM : labelHeightMm;
        const availH = Math.max(4, labelHmmEff - 2 * PRINT_MARGIN_MM);
        const maxByHeight = (availH / (Math.max(1, words.length) * 1.1)) * PX_PER_MM;
        const maxByWidth = ((isHoriz ? labelHeightMm : LABEL_W_MM) - 2 * PRINT_MARGIN_MM) / (longestLen * 0.55) * PX_PER_MM;
        const fontPx = Math.max(8, Math.min(32, maxByHeight, maxByWidth));
        return (
          <div className="font-bold flex flex-col items-center justify-center text-center w-full" style={{ fontSize: fontPx, lineHeight: 1.1 }}>
            {words.map((w, i) => <div key={i}>{w}</div>)}
          </div>
        );
      })()}
      {data.type === 'accessory' && (
        <>
          {data.primary && (
            <div className="font-bold leading-tight mb-1" style={{ fontSize: data.primary.length > 18 ? 9 : 12 }}>
              {data.primary}
            </div>
          )}
          {data.barcode && (
            <Barcode value={String(data.barcode)} format="CODE128" width={1.0} height={isHoriz ? 20 : 26} fontSize={7} margin={0} displayValue />
          )}
        </>
      )}
    </div>
  );
}

// ===================== HTML for print window =====================

// Compute number & QR sizes that always fit within a given label height.
//   labelHmm = total label height (mm)
//   numberLen = number of characters in the bike number (for width-cap)
// Returns { numberMm, qrMm }
function computeBikeSizes(labelHmm, numberLen) {
  // Reserved for: top+bottom margins (3) + talla (4) + two small gaps (~0.6)  => 7.6mm
  const reserved = 7.6;
  const remaining = Math.max(4, labelHmm - reserved);
  // Split remaining roughly 50/50 between number and QR, then clamp.
  let numberMm = Math.min(11, remaining / 2);
  let qrMm = remaining - numberMm;
  // QR clamps. If qr falls below 6, steal from number.
  if (qrMm < 6) {
    const deficit = 6 - qrMm;
    qrMm = 6;
    numberMm = Math.max(3, numberMm - deficit);
  }
  if (qrMm > 14) qrMm = 14;
  // Width cap for number (so long bike numbers like "1(53)02" fit one line).
  const len = Math.max(1, numberLen);
  const widthCap = 26 / (len * 0.55);
  numberMm = Math.max(2.8, Math.min(numberMm, widthCap));
  return { numberMm, qrMm };
}

function renderLabelHtml(data, labelHmm, qrCache = {}) {
  if (data.type === 'bicycle') {
    const { numberMm, qrMm } = computeBikeSizes(labelHmm, String(data.primary).length);
    const tallaBlock = data.size
      ? `<div class="bike-size" style="font-size: 4mm;">${escapeHtml(data.size)}</div>`
      : '';
    // Inline the pre-rendered QR SVG directly. Strip intrinsic size so it scales to mm box.
    let qrBlock = '';
    if (data.barcode) {
      const raw = qrCache[String(data.barcode)] || '';
      const svgInner = raw
        .replace(/<\?xml[^>]*\?>/i, '')
        .replace(/<svg([^>]*)>/i, (m, attrs) => {
          // Strip width/height attributes (we control size via parent box)
          const stripped = attrs.replace(/\s(width|height)="[^"]*"/gi, '');
          return `<svg${stripped} width="100%" height="100%" style="display:block;">`;
        });
      qrBlock = `<div class="qr-wrap bike-qr"><div class="qr-canvas" style="width: ${qrMm}mm; height: ${qrMm}mm;">${svgInner}</div></div>`;
    }
    return `
      <div class="bike-stack">
        <div class="number-big" style="font-size: ${numberMm.toFixed(2)}mm;">${escapeHtml(data.primary)}</div>
        ${tallaBlock}
      </div>
      ${qrBlock}
    `;
  }
  if (data.type === 'name') {
    // Each whitespace-separated word goes on its own line.
    // Font auto-shrinks so all lines fit vertically and the longest fits horizontally.
    const words = String(data.primary).split(/\s+/).filter(Boolean);
    const longestLen = words.reduce((m, w) => Math.max(m, w.length), 1);
    // Available content area (labelHmm minus margins). Width is always 29 - 2*1.5 = 26mm.
    const availH = Math.max(4, labelHmm - 2 * 1.5);  // mm
    const availW = 26;  // mm
    // Vertical: per-line height ≈ font-size * 1.1 (line-height). Solve for font-size:
    const maxByHeight = availH / (Math.max(1, words.length) * 1.1);
    // Horizontal: longest word width ≈ font-size * 0.55 * len. Solve for font-size:
    const maxByWidth = availW / (longestLen * 0.55);
    const sz = Math.max(2.5, Math.min(8, maxByHeight, maxByWidth));
    const lines = words.map(w => `<div class="name-line">${escapeHtml(w)}</div>`).join('');
    return `<div class="name-text" style="font-size: ${sz.toFixed(2)}mm;">${lines}</div>`;
  }
  const nameSize = data.primary && data.primary.length > 18 ? 3.2 : 4.4;
  return `
    ${data.primary ? `<div class="acc-name" style="font-size: ${nameSize}mm; margin-bottom: 1mm;">${escapeHtml(data.primary)}</div>` : ''}
    ${data.barcode ? `<div class="barcode-wrap"><svg class="barcode" data-value="${escapeHtml(data.barcode)}"></svg></div>` : ''}
  `;
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
